import { Component } from '@theme/component'
import { useGsap } from '@theme/gsap'

const JustCardsConfig = {
  elastic: { duration: 0.8, ease: 'elastic.out(1, 0.75)' },
  resize: { duration: 0.3, ease: 'power3.out' }
}

const MOBILE_BREAKPOINT = 991

class JustCards extends Component {
  constructor() {
    super()
    this.currentPortion = 0
    this.containerLeft = 0
    this.containerW = 0
    this.resizeHandler = null
    this.cards = []
    this.container = null
    this.gsap = null
    this.gsapContext = null
    this.timeline = null
  }

  async mounted() {
    const scope = await useGsap(this)
    if (!(scope && this.isConnected)) {
      return
    }
    this.gsap = scope.gsap
    this.gsapContext = scope.context

    this.initCards()
  }

  beforeUnmount() {
    this.cleanup()
    this.gsapContext?.revert()
    this.gsapContext = null
    this.gsap = null
  }

  cleanup() {
    if (this.timeline) {
      this.timeline.kill()
      this.timeline = null
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
    if (this.container) {
      this.container.removeEventListener('mousemove', this.handleMouseMove)
      this.container.removeEventListener('mouseenter', this.handleMouseEnter)
      this.container.removeEventListener('mouseleave', this.handlePointerLeave)
      this.container.removeEventListener('touchstart', this.handleTouchPointer)
      this.container.removeEventListener('touchmove', this.handleTouchPointer)
      this.container.removeEventListener('touchend', this.handlePointerLeave)
      this.container.removeEventListener('touchcancel', this.handlePointerLeave)
    }
    this.container = null
    this.cards = []
    this.resizeHandler = null
    this.currentPortion = 0
    this.containerLeft = 0
    this.containerW = 0
  }

  _isMobile() {
    return window.innerWidth <= MOBILE_BREAKPOINT
  }

  updateContainerBounds() {
    if (!this.container) {
      return
    }
    const rect = this.container.getBoundingClientRect()
    const width = rect.width || this.container.offsetWidth
    this.containerLeft = rect.left || 0
    this.containerW = width > 0 ? width : 0
  }

  cardTransform(el, isMobile) {
    if (isMobile) {
      return { rotation: 0, xPercent: 0, yPercent: 0 }
    }
    return {
      rotation: Number.parseFloat(el.dataset.offsetRot) || 0,
      xPercent: Number.parseFloat(el.dataset.offsetX) || 0,
      yPercent: Number.parseFloat(el.dataset.offsetY) || 0
    }
  }

  resetCards(tween = JustCardsConfig.elastic) {
    if (!this.gsapContext) {
      return
    }
    const isMobile = this._isMobile()

    this.gsapContext.add(() => {
      this.timeline?.kill()
      this.timeline = null

      if (!tween) {
        for (const card of this.cards) {
          this.gsap.set(card, {
            ...this.cardTransform(card, isMobile),
            scale: 1
          })
        }
        return
      }

      this.timeline = this.gsap.timeline()
      for (const card of this.cards) {
        this.timeline.to(
          card,
          { ...this.cardTransform(card, isMobile), scale: 1, ...tween },
          0
        )
      }
    })
  }

  focusCard(index) {
    if (!this.gsapContext || index < 0 || index >= this.cards.length) {
      return
    }
    const isMobile = this._isMobile()

    this.gsapContext.add(() => {
      this.timeline?.kill()
      this.timeline = this.gsap.timeline()

      this.cards.forEach((card, i) => {
        const base = this.cardTransform(card, isMobile)
        const isActive = i === index

        this.timeline.to(
          card,
          {
            ...base,
            rotation: isActive ? 0 : base.rotation,
            xPercent:
              isMobile || isActive
                ? base.xPercent
                : base.xPercent + 70 / (i - index),
            scale: isActive ? 1.1 : 1,
            ...JustCardsConfig.elastic
          },
          0
        )
      })
    })
  }

  handlePointerMove = (clientX) => {
    if (!this.container || this._isMobile()) {
      return
    }
    if (typeof clientX !== 'number' || Number.isNaN(clientX)) {
      return
    }
    if (!this.containerW) {
      return
    }

    const mouseX = clientX - this.containerLeft
    const portion = Math.ceil((mouseX / this.containerW) * this.cards.length)

    if (
      portion === this.currentPortion ||
      portion < 1 ||
      portion > this.cards.length
    ) {
      return
    }

    this.currentPortion = portion
    this.focusCard(portion - 1)
  }

  handleMouseEnter = () => this.updateContainerBounds()

  handleMouseMove = (e) => this.handlePointerMove(e.clientX)

  handleTouchPointer = (e) => {
    if (e.type === 'touchstart') this.updateContainerBounds()
    const touch = e.touches?.[0]
    if (touch) {
      this.handlePointerMove(touch.clientX)
    }
  }

  handlePointerLeave = () => {
    this.currentPortion = 0
    this.resetCards()
  }

  initCards() {
    const container = this.querySelector('[data-cards-container]')
    const cards = Array.from(this.querySelectorAll('[data-card-wrap]'))
    if (!container || cards.length === 0) {
      return
    }

    this.container = container
    this.cards = cards

    this.updateContainerBounds()
    this.resetCards(null)

    this.resizeHandler = () => {
      this.updateContainerBounds()
      this.resetCards(JustCardsConfig.resize)
    }
    window.addEventListener('resize', this.resizeHandler)

    this.container.addEventListener('mousemove', this.handleMouseMove)
    this.container.addEventListener('mouseenter', this.handleMouseEnter)
    this.container.addEventListener('mouseleave', this.handlePointerLeave)
    this.container.addEventListener('touchstart', this.handleTouchPointer, {
      passive: true
    })
    this.container.addEventListener('touchmove', this.handleTouchPointer, {
      passive: true
    })
    this.container.addEventListener('touchend', this.handlePointerLeave, {
      passive: true
    })
    this.container.addEventListener('touchcancel', this.handlePointerLeave, {
      passive: true
    })
  }
}

if (!customElements.get('just-cards')) {
  customElements.define('just-cards', JustCards)
}
